import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@jambushrushti/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@jambushrushti/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@jambushrushti/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@jambushrushti/components/ui/dropdown-menu";
import {
  Menu,
  Search,
  Mail,
  Scroll,
  LogOut,
  User,
  Settings,
  LayoutDashboard,
  ChevronDown,
} from "lucide-react";
import { cn } from "@jambushrushti/lib/utils";
import {
  useAdminAuth,
  ROLE_LABELS,
  UserRole,
} from "@jambushrushti/contexts/AdminAuthContext";
import { useToast } from "@jambushrushti/hooks/use-toast";

const navigationItems = [
  {
    title: "About",
    href: "/about",
    items: [
      {
        title: "About Jambushrusti",
        href: "/about",
        description: "Vision & mission of our platform",
      },
      // {
      //   title: "Gurudev Muni Jambuvijayaji",
      //   href: "/about/gurudev",
      //   description: "Life journey & spiritual contributions",
      // },
      {
        title: "Legacy of Pujyapad Muni Jambuvijayaji Maharaj",
        href: "/about/parivar",
        description: "Lineage & disciples",
      },
      {
        title: "Muni Jambuvijayaji Research Center",
        href: "/about/gyan-kendra",
        description: "Digital museum of teachings",
      },
      {
        title: "Photo Gallery",
        href: "/gallery",
        description: "Rare photographs from Gurudev's life",
      },
    ],
  },
  {
    title: "Guruvani",
    href: "/guruvani",
    description: "Sacred teachings & discourses",
  },
  {
    title: "Research",
    href: "/research",
  },
  // {
  //   title: "Learning",
  //   href: "/learning/courses",
  //   description: "Browse courses & study materials",
  // },
  {
    title: "Library",
    href: "/library",
    description: "Browse our book & manuscript collection",
  },
  {
    title: "Community",
    href: "/community",
    items: [
      {
        title: "Events",
        href: "/community/events",
        description: "Workshops & lectures",
      },
      {
        title: "Live Telecast",
        href: "/live-telecast",
        description: "Live events & broadcasts",
      },
      {
        title: "Blog",
        href: "/community/blog",
        description: "Insights & articles",
      },
      // {
      //   title: "News",
      //   href: "/community/news",
      //   description: "Latest announcements",
      // },
      {
        title: "eMagazine – Jambu Jyoti",
        href: "/emagazine",
        description: "Digital magazine archive",
      },
    ],
  },
];

const ROLE_ICONS: Record<UserRole, string> = {
  superadmin: "👑",
  admin: "🛡️",
  scholar: "📚",
  librarian: "📖",
  lms: "🎓",
  user: "👤",
};

function MobileNavItem({
  item,
  locationPathname,
  onClose,
}: {
  item: (typeof navigationItems)[number];
  locationPathname: string;
  onClose: () => void;
}) {
  const isItemActive =
    locationPathname === item.href ||
    item.items?.some(sub => locationPathname === sub.href);

  // Closed by default as requested
  const [isOpen, setIsOpen] = useState(false);

  if (!item.items) {
    return (
      <Link
        to={item.href}
        onClick={onClose}
        className={cn(
          "font-display block py-1.5 text-base font-medium transition-colors hover:text-primary",
          isItemActive && "font-semibold text-primary"
        )}
      >
        {item.title}
      </Link>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between py-1.5">
        <Link
          to={item.href}
          onClick={onClose}
          className={cn(
            "font-display flex-1 text-base font-medium transition-colors hover:text-primary",
            isItemActive && "font-semibold text-primary"
          )}
        >
          {item.title}
        </Link>
        <button
          type="button"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="rounded-md p-1.5 text-muted-foreground transition-all hover:bg-secondary/60 hover:text-foreground"
          aria-label={`Toggle ${item.title} submenu`}
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              isOpen && "rotate-180 text-primary"
            )}
          />
        </button>
      </div>

      {isOpen && (
        <div className="animate-in fade-in space-y-2 border-l-2 border-primary/30 py-1 pl-4 duration-200">
          {item.items.map(subItem => (
            <Link
              key={subItem.title}
              to={subItem.href}
              onClick={onClose}
              className={cn(
                "block py-1 text-sm",
                locationPathname === subItem.href
                  ? "font-bold text-primary"
                  : "font-medium text-foreground"
              )}
            >
              {subItem.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAdminAuth();
  const { toast } = useToast();

  // Automatically close mobile menu when switching to desktop view or changing location
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    toast({
      title: "Logged out",
      description: "You have been logged out successfully.",
    });
    navigate("/");
  };

  const getDashboardPath = (role: UserRole): string => {
    switch (role) {
      case "superadmin":
      case "admin":
        return "/admin/dashboard";
      case "librarian":
        return "/lms/dashboard";
      case "scholar":
        return "/research";
      case "lms":
        return "/learning/dashboard";
      default:
        return "/";
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
      <div className="container mx-auto flex h-16 items-center justify-between px-4 lg:px-8">
        {/* Logo */}
        <Link to="/" className="group flex items-center gap-3">
          <div className="shadow-soft group-hover:shadow-glow relative flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-gold transition-all duration-300">
            <Scroll className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="font-display text-lg font-bold leading-tight text-foreground">
              Jambushrusti
            </span>
            <span className="text-[10px] tracking-wide text-muted-foreground">
              Jain Knowledge Ecosystem
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <NavigationMenu className="hidden lg:flex">
          <NavigationMenuList className="gap-1">
            {navigationItems.map(item => {
              const isDropdownActive =
                item.items &&
                (location.pathname === item.href ||
                  item.items.some(
                    subItem =>
                      location.pathname === subItem.href ||
                      (subItem.href !== "/" &&
                        location.pathname.startsWith(subItem.href + "/"))
                  ));

              return (
                <NavigationMenuItem key={item.title} className="relative">
                  {item.items ? (
                    <>
                      <NavigationMenuTrigger
                        className={cn(
                          "h-9 bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary/50 hover:text-primary focus:outline-none",
                          isDropdownActive
                            ? "bg-secondary font-semibold text-primary"
                            : "text-foreground"
                        )}
                      >
                        {item.title}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent
                        className={
                          item.title === "Community" ? "right-0" : "left-0"
                        }
                      >
                        <ul className="grid w-[400px] gap-2 p-4 md:w-[500px] md:grid-cols-2">
                          {item.items.map(subItem => (
                            <li key={subItem.title}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={subItem.href}
                                  className={cn(
                                    "group block select-none space-y-1 rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-secondary/60 focus:bg-secondary/60",
                                    location.pathname === subItem.href &&
                                      "bg-secondary"
                                  )}
                                >
                                  <div
                                    className={cn(
                                      "text-sm font-semibold leading-none",
                                      location.pathname === subItem.href
                                        ? "font-bold text-primary"
                                        : "text-foreground"
                                    )}
                                  >
                                    {subItem.title}
                                  </div>
                                  <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">
                                    {subItem.description}
                                  </p>
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <NavigationMenuLink asChild>
                      <Link
                        to={item.href}
                        className={cn(
                          "group inline-flex h-9 w-max items-center justify-center rounded-lg bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary/50 hover:text-primary focus:bg-secondary focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50",
                          location.pathname === item.href &&
                            "bg-secondary font-semibold text-primary"
                        )}
                      >
                        {item.title}
                      </Link>
                    </NavigationMenuLink>
                  )}
                </NavigationMenuItem>
              );
            })}
          </NavigationMenuList>
        </NavigationMenu>

        {/* Right Actions */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:flex"
            asChild
          >
            <Link to="/search">
              <Search className="h-4 w-4" />
            </Link>
          </Button>
          <Button variant="subtle" size="sm" className="hidden md:flex" asChild>
            <Link to="/contact">
              <Mail className="mr-2 h-4 w-4" />
              Contact
            </Link>
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="hidden md:flex"
            asChild
          >
            <Link to="/library-admin">Library</Link>
          </Button>

          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden gap-2 md:flex"
                >
                  <span>{ROLE_ICONS[user.role]}</span>
                  <span className="max-w-[100px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {ROLE_LABELS[user.role]}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link
                    to={getDashboardPath(user.role)}
                    className="cursor-pointer"
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="hero" size="sm" className="hidden md:flex" asChild>
              <Link to="/admin">Sign In</Link>
            </Button>
          )}

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open Navigation Menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex h-full w-[300px] flex-col p-0 sm:w-[350px]"
            >
              <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-gold shadow-sm">
                    <Scroll className="h-4.5 w-4.5 text-primary-foreground" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-display text-base font-bold leading-tight text-foreground">
                      Jambushrusti
                    </span>
                    <span className="text-xs tracking-wide text-muted-foreground">
                      Jain Knowledge Ecosystem
                    </span>
                  </div>
                </Link>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-2">
                <nav className="flex flex-col gap-1 pb-4">
                  {navigationItems.map(item => (
                    <MobileNavItem
                      key={item.title}
                      item={item}
                      locationPathname={location.pathname}
                      onClose={() => setMobileOpen(false)}
                    />
                  ))}
                  <div className="flex flex-col gap-2 border-t border-border pt-4">
                    <Button
                      variant="subtle"
                      asChild
                      onClick={() => setMobileOpen(false)}
                    >
                      <Link to="/contact">Contact</Link>
                    </Button>
                    <Button
                      variant="outline"
                      asChild
                      onClick={() => setMobileOpen(false)}
                    >
                      <Link to="/library-admin">Library</Link>
                    </Button>
                    {isAuthenticated && user ? (
                      <>
                        <div className="flex items-center gap-2 rounded-lg bg-muted p-2">
                          <span>{ROLE_ICONS[user.role]}</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{user.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {ROLE_LABELS[user.role]}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          asChild
                          onClick={() => setMobileOpen(false)}
                        >
                          <Link to={getDashboardPath(user.role)}>
                            <LayoutDashboard className="mr-2 h-4 w-4" />
                            Dashboard
                          </Link>
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => {
                            handleLogout();
                            setMobileOpen(false);
                          }}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          Logout
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="hero"
                        asChild
                        onClick={() => setMobileOpen(false)}
                      >
                        <Link to="/auth">Sign In</Link>
                      </Button>
                    )}
                  </div>
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
